import type { CSSProperties } from 'react';
import type { ScoreCell, ScoreDefinition, ScoreSpread, TenantScore } from '../api.ts';
import {
  NOT_YET_ASSESSED,
  STAFF_ONLY_LABEL,
  coverageText,
  isAssessed,
  rankText,
  scoreText,
  spreadRangeText,
} from './scores.ts';
import '../scores.css';

/**
 * The pieces every staff-only score is drawn from, shared by the Scores screen,
 * the Clients rows and a client's Overview so that no one of them can show a
 * number without the counts and the label the others carry.
 */

/**
 * An outline, not a fill, and amber rather than a verdict colour: it is a
 * warning about the audience, not a status of anything it sits beside.
 */
export const StaffOnlyChip = () => <span className="sc-staff">{STAFF_ONLY_LABEL}</span>;

interface DefinitionProps {
  readonly definition: ScoreDefinition;
}

/**
 * ADR-0007's condition for any headline number — a definition printed next to
 * it — applied to staff too, because staff will quote these to customers and
 * need to be able to say how they were made.
 */
export const ScoreDefinitionNote = ({ definition }: DefinitionProps) => {
  const { critical, high, medium, low } = definition.weights;
  return (
    <div className="callout sc-definition">
      <p className="sc-definition-head">
        <span className="callout-title">How these scores are made</span>
        <StaffOnlyChip />
      </p>
      <p>{definition.summary}</p>
      <p className="note">{definition.basis}</p>
      <p className="note">
        Weights: critical {critical}, high {high}, medium {medium}, low {low}. Not applicable and
        not evidenced are outside the denominator and counted beside every score instead.
      </p>
    </div>
  );
};

interface FigureProps {
  readonly cell: ScoreCell;
  /** Drops the not-applicable count, for per-framework columns. */
  readonly short?: boolean;
}

/** A score is never rendered on its own: this is the only way one is drawn. */
export const ScoreFigure = ({ cell, short = false }: FigureProps) => (
  <span className="sc-figure">
    <span className={cell.score === null ? 'sc-score sc-unscored' : 'sc-score num'}>
      {scoreText(cell.score)}
    </span>
    <span className="sc-coverage num">{coverageText(cell, short)}</span>
  </span>
);

interface RangeProps {
  readonly spread: ScoreSpread;
  /** Where one tenant sits on the platform's range, if this line is about one. */
  readonly at?: number | null;
}

/**
 * The platform's spread on a 0–100 track: a band from minimum to maximum, a
 * tick at the median and, for one tenant, a dot. A range marker rather than a
 * filled bar on purpose — a bar filled to the score is a progress meter by
 * another name, and brief §2.1 holds even here. Hidden from assistive
 * technology because every value it draws is also written out beside it.
 */
export const ScoreRange = ({ spread, at = null }: RangeProps) => {
  if (spread.min === null || spread.max === null) return null;
  const style = {
    '--sc-from': `${spread.min}%`,
    '--sc-to': `${spread.max}%`,
    '--sc-median': `${spread.median ?? spread.min}%`,
    '--sc-at': `${at ?? 0}%`,
  } as CSSProperties;
  return (
    <span className="sc-range" style={style} aria-hidden="true" title={spreadRangeText(spread)}>
      <span className="sc-range-band" />
      <span className="sc-range-median" />
      {at !== null && <span className="sc-range-at" />}
    </span>
  );
};

interface RowLineProps {
  readonly score: TenantScore;
}

/**
 * The line under a client's name on a staff member's Clients screen. One line,
 * in label size, because this screen is ordered by attention rather than by
 * score and should stay that way: the score is context for staff, not a new
 * way to rank a customer's clients.
 */
export const StaffScoreLine = ({ score }: RowLineProps) => (
  <span className="sc-row" title={STAFF_ONLY_LABEL}>
    <span className="sc-row-tag">Staff</span>
    {isAssessed(score) ? (
      <>
        <span>
          Score <span className="sc-row-score num">{scoreText(score.overall.score)}</span>
        </span>
        <span className="num">{coverageText(score.overall)}</span>
        <span className="num">Rank {rankText(score.position)}</span>
      </>
    ) : (
      <span>{NOT_YET_ASSESSED}</span>
    )}
  </span>
);
